import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Typography,
  Box,
  CircularProgress,
  Button,
  Chip,
  IconButton,
  Snackbar,
  Alert,
  Menu,
  MenuItem,
  debounce,
  DialogContent,
  FormControl,
  DialogActions,
  Dialog,
  Select,
  DialogTitle,
  InputLabel,
  styled,
  Switch,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import {
  DataGridPro,
  GridColDef,
  GridFilterModel,
  GridRenderCellParams,
  GridSortModel,
} from "@mui/x-data-grid-pro";
import to from "await-to-js";
import { axiosWithAuth } from "../plugins/axios";
import EditIcon from "@mui/icons-material/Edit";
import LinkIcon from "@mui/icons-material/Link";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { ProblemStatementModal } from "../modal/ProblemStatementModal";
import { RubricsModal } from "../modal/RubricsModal";
import Papa from "papaparse";
import { ObjectId } from "bson";

interface Version {
  _id: string;
  versionId: string;
  status: string;
  updatedBy: string;
  prismLink: string;
  cohortName: string;
  quizName: string;
  settings: any;
  additionalInfo: any;
  quizId: string;
  crawlLinks?: boolean;
}
const commonIconButtonSx = {
  color: "black",
  transition: "transform 0.2s, color 0.2s",
  ":hover": {
    transform: "scale(1.2)",
    color: "primary.main",
  },
};

function ListOfVersions() {
  const { cohortId, quizId, assetId } = useParams();
  const navigate = useNavigate();
  const [versions, setVersions] = useState<Version[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(50);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState<string>("");
  const [toastSeverity, setToastSeverity] = useState<"success" | "error">(
    "success"
  );
  const [editingRubrics, setEditingRubrics] = useState<any>({});
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedVersion, setSelectedVersion] = useState<Version | null>(null);
  //   const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [problemStatementModalOpen, setProblemStatementModalOpen] =
    useState(false);
  const [rubricsModalOpen, setRubricsModalOpen] = useState(false);
  const [selectedProblemStatement, setSelectedProblemStatement] =
    useState<string>("");
  const [selectedRubrics, setSelectedRubrics] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [confirmationType, setConfirmationType] = useState<
    "publish" | "unpublish"
  >("publish");
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [submissionTypes, setSubmissionTypes] = useState<any[]>([]);
  const [submissionTypeModalOpen, setSubmissionTypeModalOpen] = useState(false);
  const [selectedSubmissionType, setSelectedSubmissionType] =
    useState<any>(null);

  const apiUrl = process.env.REACT_APP_PIPELINE_API_LXP;

  const debounceFetchVersions = useMemo(
    () =>
      debounce((filter) => {
        fetchVersions(filter);
      }, 600),
    []
  );
  const nestedColumnValues: { [key: string]: string } = {
    version: "version",
    status: "status",
    updatedBy: "updatedBy",
  };
  const handleFilterChange = useCallback(
    ({ items }: GridFilterModel) => {
      if (!items.length) {
        setFilterModel({ items: [] });
        debounceFetchVersions({});
        return;
      }

      const { field, value, operator } = items[0];
      const mappedFilterColumn = nestedColumnValues[field] || field;

      const filter = {
        filterColumn: mappedFilterColumn,
        filterValue: value,
        filterOperator: operator,
      };
      setFilterModel({
        items: [
          {
            field,
            value,
            operator,
          },
        ],
      });

      debounceFetchVersions(filter);
    },
    [debounceFetchVersions]
  );

  const fetchSubmissionTypes = async () => {
    const [err, response] = await to(
      axiosWithAuth(`${apiUrl}cohortConfiguration/submissiontypes`, {}, "get")
    );

    if (err) {
      setToastMessage("Failed to load submission types");
      setToastSeverity("error");
      setToastOpen(true);
    } else {
      setSubmissionTypes(response.data?.data || []);
    }
  };

  // Call this in useEffect
  useEffect(() => {
    fetchSubmissionTypes();
    // ... your existing useEffect code
  }, []);

  const fetchVersions = async (filter?: any) => {
    const filters =
      filterModel?.items.map((item) => {
        return {
          filterColumn: nestedColumnValues[item.field] || item.field,
          filterValue: item.value,
          filterOperator: item.operator,
        };
      }) || [];
    const params = {
      page: page + 1,
      limit: rowsPerPage,
      sortColumn: sortModel[0]?.field || "version",
      sortDirection: sortModel[0]?.sort || "desc",
      // filterColumn: filter?.filterColumn || null,
      // filterValue: filter?.filterValue || null,
      // filterOperator: filter?.filterOperator || null,
      filters: JSON.stringify(filters),
    };

    const [err, response] = (await to(
      axiosWithAuth(
        `${apiUrl}cohortConfiguration/assetsbyversion/${assetId}`,
        { params },
        "get"
      )
    )) as any;

    if (err) {
      setError("Failed to load versions");
      setToastMessage("Failed to load versions");
      setToastSeverity("error");
      setToastOpen(true);
    } else {
      const { data, totalCount } = response.data;
      setVersions(data);
      setTotalCount(totalCount);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchVersions();
    fetchSubmissionTypes();
  }, [cohortId, quizId, page, rowsPerPage, sortModel]);

  const handleToastClose = () => setToastOpen(false);

  const handleViewEvaluations = (rowData: any) => {
    const { cohortId, quizId, version } = rowData;
    navigate(
      `/cohort/${cohortId}/quiz/${quizId}/asset/${assetId}/version/${version}/evaluations`
    );
  };

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLButtonElement>,
    version: Version
  ) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedVersion(version);
    setConfirmationType(
      version.status === "published" ? "unpublish" : "publish"
    );
  };
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedVersion(null);
  };

  const handleOpenSubmissionTypeModal = (version: Version) => {
    setSelectedVersion(version);
    setSelectedSubmissionType(version.settings.submissionType);
    setSubmissionTypeModalOpen(true);
  };

  const handleCloseSubmissionTypeModal = () => {
    setSubmissionTypeModalOpen(false);
    setSelectedVersion(null);
    setSelectedSubmissionType(null);
  };

  const handleSubmissionTypeChange = async (submissionTypeId: string) => {
    if (!selectedVersion) return;

    const newSubmissionType = submissionTypes.find(
      (st) => st._id === submissionTypeId
    );
    if (!newSubmissionType) return;

    setIsSubmitting(true);
    try {
      await axiosWithAuth(
        `${apiUrl}cohortconfiguration/update/${selectedVersion.quizId}`,
        {
          settings: {
            ...selectedVersion.settings,
            submissionType: newSubmissionType,
          },
        },
        "put"
      );
      setToastMessage("Submission type updated successfully");
      setToastSeverity("success");
      fetchVersions(); // Refresh data
    } catch (error) {
      setToastMessage("Failed to update submission type");
      setToastSeverity("error");
    } finally {
      setIsSubmitting(false);
      setToastOpen(true);
      handleCloseSubmissionTypeModal();
    }
  };

  const handlePublishStatusChange = async () => {
    if (!selectedVersion) return;

    const newStatus =
      selectedVersion.status === "published" ? "pending" : "published";
    const actionText =
      newStatus === "published" ? "Publishing" : "Unpublishing";

    setLoading(true);
    handleMenuClose();

    const endPoint =
      selectedVersion.status === "published"
        ? `${apiUrl}cohortConfiguration/unpublish/${selectedVersion._id}`
        : `${apiUrl}cohortConfiguration/publish/${selectedVersion._id}`;

    try {
      const [err, response] = await to(
        axiosWithAuth(endPoint, { status: newStatus }, "post")
      );

      if (err) {
        setToastMessage(`Failed to update status: ${err.message}`);
        setToastSeverity("error");
      } else {
        setToastMessage(`Version ${actionText} successful`);
        setToastSeverity("success");
        fetchVersions();
      }
    } catch (error) {
      setToastMessage(
        `Error: ${error instanceof Error ? error.message : "Unknown error"}`
      );
      setToastSeverity("error");
    } finally {
      setToastOpen(true);
      setLoading(false);
    }
  };
  const handleOpenProblemModal = (
    problemStatement: string,
    version: Version
  ) => {
    setSelectedProblemStatement(problemStatement);
    setSelectedVersion(version);
    setProblemStatementModalOpen(true);
  };
  const handleOpenRubricsModal = (rubrics: string, version: Version) => {
    setSelectedRubrics({ rubric: rubrics }); // Adjust based on actual rubric structure
    setSelectedVersion(version);
    setRubricsModalOpen(true);
  };
  const handleCloseProblemModal = () => {
    setProblemStatementModalOpen(false);
    setSelectedProblemStatement("");
    setSelectedVersion(null);
  };

  const handleCloseRubricsModal = () => {
    setRubricsModalOpen(false);
    setSelectedRubrics(null);
    setSelectedVersion(null);
  };
  const handleProblemStatementChange = (content: string) => {
    setSelectedProblemStatement(content);
  };
  const handleRubricsSave = async (rubrics: any) => {
    setSelectedRubrics({ rubric: rubrics });
    setIsSubmitting(true);
    const updatedRubrics = editingRubrics;

    updatedRubrics.rubric = rubrics;

    console.log(updatedRubrics);

    const [submitErr] = (await to(
      axiosWithAuth(
        `${apiUrl}cohortconfiguration/update/${selectedVersion?.quizId}`,
        { rubrics: updatedRubrics },
        "put"
      )
    )) as any;

    if (submitErr) {
      const { data: { message = null } = {} } = submitErr.response;
      setToastMessage(message);
      setToastSeverity("error");
      setToastOpen(true);
    } else {
      handleCloseRubricsModal();
      setIsSubmitting(false);
    }
    setIsSubmitting(false);
  };

  const handleProblemSubmit = async () => {
    if (!selectedVersion) return;

    setIsSubmitting(true);
    try {
      await axiosWithAuth(
        `${apiUrl}cohortconfiguration/update/${selectedVersion?.quizId}`,
        { problemStatement: selectedProblemStatement },
        "put"
      );
      setToastMessage("Problem statement updated successfully");
      setToastSeverity("success");
      fetchVersions(); // Refresh data
    } catch (error) {
      setToastMessage("Failed to update problem statement");
      setToastSeverity("error");
    } finally {
      setIsSubmitting(false);
      setToastOpen(true);
      handleCloseProblemModal();
    }
  };
  const handleRubricsSubmit = async () => {
    if (!selectedVersion) return;
    setIsSubmitting(true);
    try {
      (await axiosWithAuth(
        `${apiUrl}cohortconfiguration/update/${selectedVersion?.quizId}`,
        { rubrics: selectedRubrics.rubric },
        "put"
      )) as any;
      // await axiosWithAuth(
      //   `${apiUrl}autograde/versions/${cohortId}/${quizId}/${selectedVersion._id}`,
      //   { rubrics: selectedRubrics.rubric },
      //   "post"
      // );
      setToastMessage("Rubrics updated successfully");
      setToastSeverity("success");
      fetchVersions(); // Refresh data
    } catch (error) {
      setToastMessage("Failed to update rubrics");
      setToastSeverity("error");
    } finally {
      setIsSubmitting(false);
      setToastOpen(true);
      handleCloseRubricsModal();
    }
  };
  // const handleRubricsDownload = () => {
  //   const dataStr = `data:text/json;charset=utf-8,${encodeURIComponent(
  //     JSON.stringify(selectedRubrics.rubric)
  //   )}`;
  //   const link = document.createElement("a");
  //   link.setAttribute("href", dataStr);
  //   link.setAttribute("download", `rubrics_${selectedVersion?.versionId}.json`);
  //   link.click();
  // };

  function processRubricToCsv(json: any): string {
    const rows: string[] = [];
    const printedL1 = new Set<string>();
    const printedL2 = new Set<string>();

    rows.push(
      '"Level 1","Level 2","Level 3","Max Marks","Comments","Marks","Grading Guidelines"'
    );

    function processNode(
      node: any,
      l1: any = {},
      l2: any = {},
      prevL1Id: string | null = null,
      prevL2Id: string | null = null
    ): void {
      if (node.children && node.children.length > 0) {
        node.children.forEach((child: any) => {
          if (!l1.id) {
            processNode(
              child,
              { id: node.id, name: node.name, maxMarks: node.maxMarks },
              {},
              null,
              null
            );
          } else if (!l2.id) {
            processNode(
              child,
              l1,
              { id: node.id, name: node.name, maxMarks: node.maxMarks },
              l1.id,
              null
            );
          } else {
            processNode(child, l1, l2, l1.id, l2.id);
          }
        });
      } else {
        if (!l1.name) {
          rows.push(
            `"${node.name || ""}","","","${node.maxMarks || ""}","","","${(
              node.gradingGuideline || ""
            )
              .replace(/\n/g, " ")
              .replace(/"/g, '""')}"`
          );
        } else if (!l2.name) {
          if (!l1.name || !printedL1.has(l1.name)) {
            rows.push(
              `"${l1.name || ""}","","","${l1.maxMarks || ""}","","",""`
            );
            printedL1.add(l1.name);
          }
          rows.push(
            `"${l1.name || ""}","${node.name || ""}","","${
              node.maxMarks || ""
            }","","","${(node.gradingGuideline || "")
              .replace(/\n/g, " ")
              .replace(/"/g, '""')}"`
          );
        } else {
          if (!l1.name || !printedL1.has(l1.name)) {
            rows.push(
              `"${l1.name || ""}","","","${l1.maxMarks || ""}","","",""`
            );
            printedL1.add(l1.name);
          }

          if (!l2.name || !printedL2.has(l2.name)) {
            rows.push(
              `"${l1.name || ""}","${l2.name || ""}","","${
                l2.maxMarks || ""
              }","","","",""`
            );
            printedL2.add(l2.name);
          }

          rows.push(
            `"${l1.name || ""}","${l2.name || ""}","${node.name || ""}","${
              node.maxMarks || ""
            }","","","${(node.gradingGuideline || "")
              .replace(/\n/g, " ")
              .replace(/"/g, '""')}"`
          );
        }
      }
    }

    json.rubric.children.forEach((child: any) => {
      processNode(child);
    });

    return rows.join("\n");
  }

  const handleRubricsDownload = () => {
    const csvOutput = processRubricToCsv(editingRubrics);
    const dataStr =
      "data:text/csv;charset=utf-8," + encodeURIComponent(csvOutput);
    const downloadAnchorNode = document.createElement("a");
    downloadAnchorNode.setAttribute("href", dataStr);
    downloadAnchorNode.setAttribute(
      "download",
      `rubrics_${selectedVersion?.quizId}.csv`
    );

    document.body.appendChild(downloadAnchorNode);
    downloadAnchorNode.click();
    downloadAnchorNode.remove();
  };

  const handleRubricsUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    // const file = event.target.files?.[0];
    // if (file) {
    //   const reader = new FileReader();
    //   reader.onload = (e) => {
    //     try {
    //       const json = JSON.parse(e.target?.result as string);
    //       setSelectedRubrics({ rubric: json });
    //       handleRubricsSubmit(); // Auto-save after upload
    //     } catch (error) {
    //       setToastMessage("Invalid JSON file");
    //       setToastSeverity("error");
    //       setToastOpen(true);
    //     }
    //   };
    //   reader.readAsText(file);
    // }

    const file = event.target.files?.[0];
    setEditingRubrics({});

    if (file) {
      try {
        const text = await file.text();
        let parsedRubrics;

        if (file.name.endsWith(".csv")) {
          parsedRubrics = await processCsvToHierarchy(text);
        } else {
          throw new Error("Unsupported file format");
        }

        console.log(parsedRubrics);

        setEditingRubrics(parsedRubrics);
        setSelectedRubrics(parsedRubrics);
      } catch (error) {
        if (error instanceof Error) {
          setToastMessage(error.message);
          setToastSeverity("error");
          setToastOpen(true);
        } else {
          setToastMessage("Invalid file format or content");
          setToastSeverity("error");
          setToastOpen(true);
        }
      }
    }
  };

  const processCsvToHierarchy = async (csvData: string) => {
    const hierarchy = {
      rubric: {
        id: 0,
        children: [],
        maxMarks: 0,
      },
      published: editingRubrics?.published || false,
      canBeUnpublished: editingRubrics?.canBeUnpublished || false,
      questionId: editingRubrics?.questionId || 0,
      questionTitle: editingRubrics?.questionTitle || "",
    };

    function getNextId() {
      return new ObjectId().toHexString();
    }

    function findOrCreateLevel(
      levelArray: any[],
      name: string,
      maxMarks: number | null,
      isLeaf = false
    ) {
      let existing = levelArray.find((item) => item.name === name);
      if (!existing) {
        existing = {
          id: getNextId(),
          name: name,
          maxMarks: maxMarks || 0,
          gradingGuideline: null,
          ...(isLeaf ? {} : { children: [] }),
        };
        levelArray.push(existing);
      }
      return existing;
    }

    return new Promise((resolve, reject) => {
      Papa.parse(csvData, {
        header: true,
        skipEmptyLines: true,
        complete: (result) => {
          try {
            const rows = result.data;

            rows.forEach((row: any) => {
              const level1 = row["Level 1"];
              const level2 = row["Level 2"];
              const level3 = row["Level 3"];
              const maxMarks = parseInt(row["Max Marks"] || "0", 10);
              const gradingGuideline = row["Grading Guidelines"] || null;

              let level1Node = findOrCreateLevel(
                hierarchy.rubric.children,
                level1,
                null
              );

              let level2Node = null;
              if (level2) {
                level2Node = findOrCreateLevel(
                  level1Node.children,
                  level2,
                  null
                );
              }

              if (level3) {
                const level3Node = findOrCreateLevel(
                  level2Node.children,
                  level3,
                  maxMarks,
                  true
                );
                level3Node.gradingGuideline = gradingGuideline;
              }
            });

            hierarchy.rubric.children.forEach((child: any) =>
              validateHierarchy(child, 1)
            );
            updateMaxMarks(hierarchy.rubric);
            validateChildMarks(hierarchy.rubric);

            resolve(hierarchy);
          } catch (error) {
            reject(error);
          }
        },
        error: (error: Error) => {
          reject(error);
        },
      });
    });
  };

  function validateHierarchy(node: any, level: number) {
    if (level === 1 && (!node.children || node.children.length === 0)) {
      throw new Error(
        `Validation Error: Level 1 node "${node.name}" has no Level 2 children.`
      );
    }
    if (level === 2 && (!node.children || node.children.length === 0)) {
      throw new Error(
        `Validation Error: Level 2 node "${node.name}" has no Level 3 children.`
      );
    }
    if (node.children) {
      node.children.forEach((child: any) =>
        validateHierarchy(child, level + 1)
      );
    }
  }

  function validateChildMarks(node: any) {
    if (node.children && node.children.length > 0) {
      const childrenSum = node.children.reduce(
        (sum: number, child: any) => sum + child.maxMarks,
        0
      );

      if (node.maxMarks !== childrenSum) {
        throw new Error(
          `Validation Error: Max marks of node "${node.name}" (ID: ${node.id}) is ${node.maxMarks}, which does not match the sum of its children's marks (${childrenSum}).`
        );
      }

      node.children.forEach(validateChildMarks);
    }
  }

  function updateMaxMarks(node: any) {
    if (node.children) {
      node.maxMarks = node.children.reduce((sum: number, child: any) => {
        updateMaxMarks(child);
        return sum + child.maxMarks;
      }, 0);
    }
  }

  const AntSwitch = styled(Switch)(({ theme }) => ({
    width: 36,
    height: 16,
    padding: 0,
    display: "flex",
    "&:active": {
      "& .MuiSwitch-thumb": {
        width: 15,
      },
      "& .MuiSwitch-switchBase.Mui-checked": {
        transform: "translateX(14px)",
      },
    },
    "& .MuiSwitch-switchBase": {
      padding: 2,
      "&.Mui-checked": {
        transform: "translateX(20px)",
        color: "#fff",
        "& + .MuiSwitch-track": {
          opacity: 1,
          backgroundColor: "#1890ff",
        },
      },
    },
    "& .MuiSwitch-thumb": {
      boxShadow: "0 2px 4px 0 rgb(0 35 11 / 20%)",
      width: 12,
      height: 12,
      borderRadius: 6,
      transition: theme.transitions.create(["width"], {
        duration: 200,
      }),
    },
    "& .MuiSwitch-track": {
      borderRadius: 16 / 2,
      width: "100%",
      opacity: 1,
      backgroundColor: "rgba(0,0,0,.25)",
    },
  }));

  const handleToggleExtractLinks = async (
    versionId: string,
    quizId: string,
    shouldCrawl: boolean
  ) => {
    try {
      // Optimistic UI update
      setVersions((prevVersions) =>
        prevVersions.map((version) =>
          version._id === versionId
            ? { ...version, crawlLinks: shouldCrawl }
            : version
        )
      );

      await axiosWithAuth(
        `${apiUrl}cohortconfiguration/update/${quizId}`,
        { crawlLinks: shouldCrawl },
        "put"
      );

      setToastMessage(
        `Link extraction ${shouldCrawl ? "enabled" : "disabled"}`
      );
      setToastSeverity("success");
    } catch (error) {
      // Revert on error
      setVersions((prevVersions) =>
        prevVersions.map((version) =>
          version._id === versionId
            ? { ...version, crawlLinks: !shouldCrawl }
            : version
        )
      );
      setToastMessage("Failed to update link extraction");
      setToastSeverity("error");
    } finally {
      setToastOpen(true);
    }
  };

  const versionColumns: GridColDef[] = [
    {
      field: "version",
      headerName: "Versions",
      width: 200,
      renderCell: (params) => {
        return (
          <Button
            color="primary"
            onClick={() => handleViewEvaluations(params.row)}
          >
            {params.row.version}
          </Button>
        );
      },
    },
    {
      field: "problemStatement",
      headerName: "problem Statement",
      align: "center",
      headerAlign: "center",
      sortable: false,
      disableColumnMenu: true,
      width: 250,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <IconButton
            sx={commonIconButtonSx}
            onClick={() =>
              handleOpenProblemModal(
                params.row.problemStatement,
                params.row as Version
              )
            }
          >
            <EditIcon />
          </IconButton>
        );
      },
    },
    {
      field: "rubrics",
      headerName: "Rubrics",
      width: 200,
      align: "center",
      headerAlign: "center",
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams) => (
        <IconButton
          sx={commonIconButtonSx}
          onClick={() => {
            handleOpenRubricsModal(
              params.row?.updatedRubrics?.rubric ?? params.row?.rubrics?.rubric,
              params.row as Version
            );
            setEditingRubrics(
              params.row?.updatedRubrics || params.row?.rubrics
            );
          }}
        >
          <EditIcon />
        </IconButton>
      ),
    },
    // {
    //   field: "settings",
    //   headerName: "Submission Type",
    //   width: 200,
    //   renderCell: (params: GridRenderCellParams) => {
    //     let val: string | undefined = params.row.settings?.submissionType?.name;

    //     return val || "-"
    //   },
    //   filterable: false,
    //   sortable: false,
    // },
    {
      field: "settings",
      headerName: "Submission Type",
      width: 250,
      renderCell: (params: GridRenderCellParams) => {
        const submissionType = params.row.settings?.submissionType;
        return (
          <Box display="flex" alignItems="center">
            <IconButton
              sx={{
                ...commonIconButtonSx,
                marginRight: 1,
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleOpenSubmissionTypeModal(params.row as Version);
              }}
              disabled={params.row.status === "published"}
            >
              <EditIcon fontSize="small" />
            </IconButton>
            {submissionType?.name || "-"}
          </Box>
        );
      },
      filterable: false,
      sortable: false,
    },
    {
      field: "crawlLinks",
      headerName: "Extract Links",
      width: 150,
      renderCell: (params) => (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          width="100%"
          height="100%"
        >
          <AntSwitch
            checked={Boolean(params.value)}
            onChange={(e) =>
              handleToggleExtractLinks(
                params.row._id,
                params.row.quizId,
                e.target.checked
              )
            }
            disabled={params.row.status === "published"}
          />
        </Box>
      ),
      filterable: false,
    },
    {
      field: "status",
      headerName: "Status",
      align: "center",
      headerAlign: "center",
      width: 220,
      renderCell: (params) => (
        <Chip
          label={params.row.status}
          variant="outlined"
          sx={{
            borderRadius: 10,
            padding: "8px 25px",
            borderColor:
              params.row.status === "published" ? "green" : "default",
            color: params.row.status === "published" ? "green" : "default",
          }}
        />
      ),
    },

    {
      field: "submissionCount",
      headerName: "Submissions Available",
      align: "center",
      headerAlign: "center",
      width: 220,
      renderCell: (params) =>
        params.row?.submissionCount ? (
          <Box>{params.row?.submissionCount}</Box>
        ) : (
          "-"
        ),
    },

    {
      field: "updatedBy",
      headerName: "Updated By",
      width: 250,
      renderCell: (params) =>
        params.row.updatedBy ? <Box>{params.row.updatedBy}</Box> : "-",
    },
    {
      field: "prismLink",
      headerName: "Prism Link",
      width: 200,
      align: "center",
      headerAlign: "center",
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams) => (
        <IconButton sx={commonIconButtonSx}>
          <a
            href={process.env.REACT_APP_PRISM_ASSET_LIBRARY}
            target="_blank"
            rel="noopener noreferrer"
          >
            <LinkIcon sx={{ transform: "rotate(-45deg)", color: "black" }} />
          </a>
        </IconButton>
      ),
    },
    {
      field: "action",
      headerName: "",
      align: "center",
      headerAlign: "center",
      sortable: false,
      disableColumnMenu: true,
      flex: 4,
      renderCell: (params: GridRenderCellParams) => (
        <>
          <IconButton
            sx={commonIconButtonSx}
            onClick={(event) => handleMenuOpen(event, params.row as Version)}
            aria-label="more options"
          >
            <MoreVertIcon />
          </IconButton>
        </>
      ),
    },
  ];

  return (
    <Box mt={4}>
      <Typography variant="h4" gutterBottom>
        List of Versions
      </Typography>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        mt={2}
      >
        <Typography gutterBottom>
          Asset Name: {""}
          <Typography display={"inline"} fontWeight={"bold"}>
            {versions?.[0]?.quizName}
          </Typography>
        </Typography>
        <Typography gutterBottom>
          Program Name:{""}
          <Typography display={"inline"} fontWeight={"bold"}>
            {
              versions?.find((v) => v?.additionalInfo?.usages?.[0]?.programName)
                ?.additionalInfo?.usages?.[0]?.programName
            }
          </Typography>
        </Typography>
        <Typography gutterBottom>
          Submission Type : {""}
          <Typography display={"inline"} fontWeight={"bold"}>
            {versions?.[0]?.settings?.submissionType.name}
          </Typography>
        </Typography>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      ) : (
        <DataGridPro
          rows={versions}
          columns={versionColumns}
          paginationMode="server"
          rowCount={totalCount}
          paginationModel={{ page, pageSize: rowsPerPage }}
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setRowsPerPage(model.pageSize);
          }}
          pageSizeOptions={[5, 10, 20, 50, 100]}
          disableRowSelectionOnClick
          getRowId={(row) => row._id}
          autoHeight
          onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
          onFilterModelChange={handleFilterChange}
        />
      )}

      {/* // Add this near your other modals */}
      <Dialog
        open={submissionTypeModalOpen}
        onClose={handleCloseSubmissionTypeModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Edit Submission Type</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Submission Type</InputLabel>
            <Select
              value={selectedSubmissionType?._id || ""}
              onChange={(e) =>
                setSelectedSubmissionType(
                  submissionTypes.find((st) => st._id === e.target.value)
                )
              }
              label="Submission Type"
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 300,
                    overflowY: "auto",
                  },
                },
              }}
            >
              {submissionTypes.map((type) => (
                <MenuItem key={type._id} value={type._id}>
                  {type.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSubmissionTypeModal}>Cancel</Button>
          <Button
            onClick={() =>
              selectedSubmissionType &&
              handleSubmissionTypeChange(selectedSubmissionType._id)
            }
            disabled={isSubmitting || !selectedSubmissionType}
            color="primary"
          >
            {isSubmitting ? <CircularProgress size={24} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        {selectedVersion && (
          <MenuItem onClick={handlePublishStatusChange}>
            {selectedVersion.status === "published" ? "Un-Publish" : "Publish"}
          </MenuItem>
        )}
      </Menu>
      <Snackbar
        open={toastOpen}
        autoHideDuration={6000}
        onClose={handleToastClose}
      >
        <Alert
          onClose={handleToastClose}
          severity={toastSeverity}
          sx={{ width: "100%" }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
      <ProblemStatementModal
        open={problemStatementModalOpen}
        onClose={handleCloseProblemModal}
        problemStatement={selectedProblemStatement}
        onChange={handleProblemStatementChange}
        onSubmit={handleProblemSubmit}
        isSubmitting={isSubmitting}
        isPublished={selectedVersion?.status === "published"}
      />

      {/* Rubrics Modal */}
      <RubricsModal
        open={rubricsModalOpen}
        onClose={handleCloseRubricsModal}
        rubrics={selectedRubrics}
        onDownload={handleRubricsDownload}
        onUpload={handleRubricsUpload}
        onSave={handleRubricsSave}
        isSubmitting={isSubmitting}
        isPublished={selectedVersion?.status === "published"}
      />
    </Box>
  );
}

export default ListOfVersions;
